import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { reduxForm } from 'redux-form';

import { fetchEvaluation, saveEvaluation, submitEvaluation } from 'actions/CourseEvaluation';

import { FormattedMessage } from 'react-intl';

import flowRight from 'lodash/flowRight';
import map from 'lodash/map';
import merge from 'lodash/merge';
import find from 'lodash/find';

import PanelMessage from 'components/panels/PanelMessage';
import Loading from 'components/Loading';
import QAElement from 'components/QAElement';

import { Col } from 'react-flexbox-grid';

import Paper from 'material-ui/Paper';
import Subheader from 'material-ui/Subheader';
import RaisedButton from 'material-ui/RaisedButton';
import ListItem from 'components/KJRN/KListItem';
import CircularProgress from 'material-ui/CircularProgress';

const styles = {
  header: {
    paddingLeft: 0,
    fontSize: 20,
    fontWeight: 300,
    fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif',
  },
  info: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  infoItem: {
    minWidth: 200,
    display: 'flex',
    flex: 1,
  },
};

const validate = (values, props) => {
  const error = {};
  const { evaluation } = props;
  if (!evaluation) { return error; }
  const questions = evaluation.CourseEvaluation.QAElements.filter(q => q.QAElementType === 3);

  map(questions, ({
    QAElementID,
    QAInput,
    MinAnswers,
  }) => {
    /* No validation on checkbox questions */
    if (QAInput.QAInputAnswerType === 3) { return; }
    const key = `${QAInput.QAInputAnswerType}-${QAElementID}`;
    /* First check if element is equired */
    if (MinAnswers > 0 && !values[key]) {
      error[key] = 'Þetta svæði verður að vera útfyllt';
      /* Second check if element has mask */
    } else if (QAInput && QAInput.QAMaskEntity) {
      if (QAInput.QAInputAnswerType === 5) { return; }
      const { QAMaskString, QAMaskDescription } = QAInput.QAMaskEntity;
      /* Create regex for the mask */
      const re = new RegExp(QAMaskString, 'i');
      if (!re.test(values[key])) {
        error[key] = QAMaskDescription;
      }
    }
  });

  return error;
};

class CourseEvaluation extends React.Component {
  componentDidMount() {
    this.props.fetch(this.props.routeParams.CourseAttendeeID);
    window.scrollTo(0, 0);
  }

  save = (data, dispatch) => dispatch(saveEvaluation(data, this.props.routeParams.CourseAttendeeID));
  submit = (data, dispatch) => dispatch(submitEvaluation(data, this.props.routeParams.CourseAttendeeID));

  render() {
    const {
      evaluationSaving,
      evaluationSubmitting,
      evaluationError,
      hasEvaluationError,
      handleSubmit,
      evaluation,
      loaded,
      invalid,
    } = this.props;

    if (hasEvaluationError) {
      return <PanelMessage message={evaluationError && (evaluationError.message || evaluationError.Message)} type="error" iconName="error" />;
    }

    if (!loaded) {
      return <Loading />;
    }

    const isSubmitted = (
      evaluation &&
      evaluation.CourseAttendee &&
      evaluation.CourseAttendee.EvaluationStatus === 2
    );


    return (
      <Col xs={12} md={10} mdOffset={1} style={{ paddingBottom: 16 }} >
        <Subheader style={styles.header}><FormattedMessage id="courseEval.title" /></Subheader>
        {isSubmitted &&
          <PanelMessage
            type="success"
            iconName="check"
            message="Búið er að skila þessu námskeiðsmati"
          />
        }
        <Paper style={styles.info}>
          <div style={styles.infoItem}>
            <ListItem
              disabled
              primaryText={evaluation.CourseAttendee.CourseName}
              secondaryText="Námskeið"
            />
          </div>
          <div style={styles.infoItem}>
            <ListItem
              disabled
              primaryText={evaluation.CourseEvaluation.CourseEvaluationName}
              secondaryText="Námskeiðsmat"
            />
          </div>
        </Paper>
        <form>
          {evaluation.CourseEvaluation.QAElements.map(e =>
          (<QAElement
            key={e.QAElementID}
            disabled={isSubmitted}
            {...e}
          />))}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <RaisedButton
              label={<FormattedMessage id="courseEval.save" />}
              primary
              icon={evaluationSaving && <CircularProgress style={{ lineHeight: '16px' }} size={16} thickness={2} />}
              disabled={evaluationSaving || evaluationSubmitting || isSubmitted}
              onClick={handleSubmit(this.save)}
            />
            <RaisedButton
              label={<FormattedMessage id="courseEval.submit" />}
              icon={evaluationSubmitting && <CircularProgress style={{ lineHeight: '16px' }} size={16} thickness={2} />}
              secondary
              style={{ marginLeft: 16 }}
              disabled={invalid || evaluationSaving || evaluationSubmitting || isSubmitted}
              onClick={handleSubmit(this.submit)}
            />
          </div>
        </form>
      </Col>
    );
  }
}

CourseEvaluation.propTypes = {
  routeParams: PropTypes.shape({
    CourseAttendeeID: PropTypes.string.isRequired,
  }),
  handleSubmit: PropTypes.func.isRequired,
  fetch: PropTypes.func.isRequired,
  evaluationError: PropTypes.shape({
    message: PropTypes.string,
    Message: PropTypes.string,
  }),
  evaluation: PropTypes.shape({
    CourseAttendee: PropTypes.shape({
      CourseName: PropTypes.string,
      EvaluationStatus: PropTypes.oneOf([0, 1, 2]),
    }),
    CourseEvaluation: PropTypes.shape({
      CourseEvaluationName: PropTypes.string,
      QAElements: PropTypes.array,
    }),
  }),
  hasEvaluationError: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  evaluationSaving: PropTypes.bool.isRequired,
  evaluationSubmitting: PropTypes.bool.isRequired,
};

const answersFromEvaluation = (evaluation) => {
  if (!evaluation || !evaluation.CourseEvaluation || !evaluation.CourseEvaluationAnswers) {
    return {};
  }
  const QAAnswers = evaluation.CourseEvaluationAnswers;
  const { QAElements } = evaluation.CourseEvaluation;
  const elems = map(
    QAAnswers,
    ({
      QAElementID,
      QAAnswerText,
      QAAnswerValue,
      QAAnswerComment,
    }) => {
      const { QAInput } = find(QAElements, ['QAElementID', QAElementID]);
      if (!QAInput) { return {}; }
      const { QAInputAnswerType } = QAInput;
      const key = `${QAInputAnswerType}-${QAElementID}${(QAInputAnswerType === 3 ? `-${btoa(QAAnswerText)}` : '')}`;
      if (QAInputAnswerType === 3) {
        return { [key]: QAAnswerValue === 'true', [`COMMENT-${QAInputAnswerType}-${QAElementID}`]: QAAnswerComment };
      } else if (QAInputAnswerType === 2 || QAInputAnswerType === 6) {
        return { [key]: Number(QAAnswerValue), [`COMMENT-${QAInputAnswerType}-${QAElementID}`]: QAAnswerComment };
      }
      return { [key]: QAAnswerValue, [`COMMENT-${QAInputAnswerType}-${QAElementID}`]: QAAnswerComment };
    },
  );
  // Merge the array of fields into one state object.
  return merge(...elems);
};

export default flowRight(
  connect(
    ({ CourseEvaluationReducer }, { routeParams }) => ({
      ...CourseEvaluationReducer,
      evaluation: CourseEvaluationReducer.evaluationById[routeParams.CourseAttendeeID],
      loaded: CourseEvaluationReducer.evaluationLoadedById[routeParams.CourseAttendeeID] || false,
      initialValues: answersFromEvaluation(CourseEvaluationReducer.evaluationById[routeParams.CourseAttendeeID]),
    }),
    dispatch => ({
      fetch: id => dispatch(fetchEvaluation(id)),
    }),
  ),
  reduxForm({
    form: 'evaluation',
    enableReinitialize: true,
    validate,
  }),
)(CourseEvaluation);



// WEBPACK FOOTER //
// ./src/containers/CourseEvaluation.js